import { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import auth from "components/nux/auth";
import { PaymentMethod } from "@stripe/stripe-js";
import Checkbox from "components/common/Checkbox";
import { useAcceptMemberPlan, useAddMemberTasks } from "../../..//services/nux";
import { getStepPath, parseState } from "components/nux/utils";
import NuxContentLayout from "components/nux/NuxContentLayout";
import useIsMobile from "../../..//components/hooks/useIsMobile";
import { USER_NUX_TYPE } from "components/nux/constants";
import { useGetMemberDetails } from "services/member";
import { useCreateMemberBilling } from "services/billing";
import CurrentUserAvatar from "components/common/CurrentUserAvatar";
import { Lock } from "lucide-react";
import MemberPayment from "components/billing/MemberPayment";
import { TOS_VERSION } from "components/context/CheckTerms";
import { NuxDetailsContext } from "components/context/AdvisorNuxContext";
import { FamilyStatus } from "protogen/advisors_service_pb";

// const formateStartDate = (dateString: string): string => {
//   let date = new Date(dateString);
//   let options: Intl.DateTimeFormatOptions = {
//     year: "numeric",
//     month: "long",
//     day: "numeric",
//   };
//   return date.toLocaleDateString("en-US", options);
// };
export default () => {
  const memberPaymentFormRef = useRef({
    submitForm: async () => Promise<void>,
  });
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const nuxDetails = useContext(NuxDetailsContext);
  const [serviceDetails, setServiceDetails] = useState<string>("");
  const [hasAcceptedToS, setHasAcceptedToS] = useState(false);
  const [hasAcceptedBilling, setHasAcceptedBilling] = useState(false);
  const [memberFormError, setMemberFormError] = useState<string | null>(null);
  const { request: createMemberBillingRequest, error } =
    useCreateMemberBilling();
  const { request: addMemberTasksRequest, loading: addMemberTasksLoading } =
    useAddMemberTasks();

  const { request: acceptMemberPlanRequest, loading: acceptLoading } =
    useAcceptMemberPlan();
  let isTruncated = false;
  if (nuxDetails.state) {
    const state = parseState(nuxDetails.state);
    isTruncated = !!state.isTruncated;
  }

  const {
    request: getMemberRequest,
    data,
    loading: memberLoading,
  } = useGetMemberDetails();
  useEffect(() => {
    getMemberRequest();
  }, []);
  const alreadyHasBilling = !!data?.family?.billingInfo?.stripeCustomerId;
  const isTestFamily = data?.family?.status === FamilyStatus.TEST;
  useEffect(() => {
    try {
      const nuxState = JSON.parse(nuxDetails?.state);
      setServiceDetails(nuxState.serviceDetails);
    } catch (e) {
      setServiceDetails("");
    }
  }, [nuxDetails]);

  const onStripeSuccess = async (pm: PaymentMethod) => {
    if (!pm.id) {
      return;
    }
    const result = await createMemberBillingRequest({
      familyRef: data?.family.ref,
      paymentMethodId: pm.id,
    });
    if (result === null) {
      throw error;
    }
  };

  const onContinue = async () => {
    if (!hasAcceptedToS || !hasAcceptedBilling || !data) return;
    if (!memberPaymentFormRef.current) {
      return;
    }
    setMemberFormError(null);
    if (!(alreadyHasBilling || isTestFamily)) {
      setLoading(true);
      try {
        await memberPaymentFormRef.current.submitForm();
      } catch (e) {
        if (e instanceof Error) {
          setMemberFormError(e.message);
        }
        setLoading(false);
        return;
      }
      setLoading(false);
    }
    const resp = await acceptMemberPlanRequest({
      token: auth.getAuthData(),
      termsOfServiceVersion: TOS_VERSION,
    });
    if (isTruncated) {
      // This triggers the member activation.
      await addMemberTasksRequest({
        token: auth.getAuthData(),
        tasks: [],
      });
    }

    if (resp?.nuxDetails?.stepName) {
      const path = getStepPath(resp.nuxDetails.stepName, USER_NUX_TYPE.MEMBER);
      navigate(path);
    }
  };
  let firstMonthCost = data?.family?.billingInfo?.product?.amountCents;
  if (data?.family?.billingInfo?.introCoupon?.id) {
    if (data?.family?.billingInfo?.introCoupon.amountOffCents) {
      firstMonthCost -= data?.family?.billingInfo?.introCoupon.amountOffCents;
    } else {
      firstMonthCost -=
        firstMonthCost *
        (data?.family?.billingInfo?.introCoupon.percentOff / 100);
    }
  }
  const overallLoading =
    memberLoading || loading || acceptLoading || addMemberTasksLoading;
  useEffect(() => {
    setMemberFormError(error);
  }, [error]);
  return (
    <NuxContentLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "48px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          <Typography variant="title">Plan details</Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <Typography variant="h4">Plan agreement</Typography>
          <Box
            sx={{
              display: "flex",
              gap: "16px",
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <Box
              sx={(theme) => ({
                width: isMobile ? "100%" : "50%",
                display: "flex",
                gap: "16px",
                padding: "16px",
                border: `1px solid ${theme.palette.border}`,
                borderRadius: "12px",
              })}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <CurrentUserAvatar
                  user={data?.primaryAdvisor}
                  size={60}
                  publicLink={true}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography variant="h3Serif">
                  {data?.primaryAdvisor?.displayName || ""}
                </Typography>
                <Typography variant="bodySmallHeavy" color="text.secondary">
                  Faye Advisor
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: isMobile ? "100%" : "50%",
                borderRadius: "12px",
                backgroundColor: "#F9FAFA",
                padding: "24px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="h4">Monthly rate</Typography>
                  <Typography variant="h4" sx={{ fontSize: "20px" }}>
                    {firstMonthCost
                      ? `$${(firstMonthCost / 100).toFixed(2)}`
                      : ""}
                  </Typography>
                </Box>
                {!!data?.family?.billingInfo?.introCoupon?.id && (
                  <Box
                    sx={{
                      marginTop: isMobile ? "8px" : "16px",
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="bodyHeavy"
                      color="#6B6E7B"
                      sx={{ fontWeight: 500 }}
                    >
                      After trial ends
                    </Typography>
                    <Typography
                      variant="bodyHeavy"
                      color="#6B6E7B"
                      sx={{ fontWeight: 500 }}
                    >
                      $
                      {(
                        data?.family?.billingInfo?.product?.amountCents / 100
                      ).toFixed(2)}
                      /mo
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        {serviceDetails && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Typography variant="h3Serif">
              Notes from {data?.primaryAdvisor?.firstName || ""}
            </Typography>
            <Typography variant="body">{serviceDetails}</Typography>
          </Box>
        )}
        {isTestFamily && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <Typography variant="h3Serif">Payment information</Typography>
              <Lock size={16} color="#8E9598" />
            </Box>
            <Typography variant="body">
              This is a test family, no payment information is required.
            </Typography>
          </Box>
        )}
        {!(alreadyHasBilling || isTestFamily) && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <Typography variant="h3Serif">Payment information</Typography>
              <Lock size={16} color="#8E9598" />
            </Box>
            <Typography variant="body">
              We’ll charge your card today, but you won’t be billed again until
              one month after the onboarding call with your Advisor. You can
              cancel your service at any time.
            </Typography>
            {data?.family && data?.member && (
              <Box sx={{}}>
                <MemberPayment
                  family={data?.family}
                  member={data?.member}
                  ref={memberPaymentFormRef}
                  onStripeSuccess={async (pm) => {
                    await onStripeSuccess(pm);
                  }}
                />
              </Box>
            )}
            <Box>
              {memberFormError && (
                <Typography variant="body" color="error">
                  {memberFormError}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <Checkbox
              checked={hasAcceptedToS}
              onChange={() => {
                setHasAcceptedToS(!hasAcceptedToS);
              }}
            />
            <Typography variant="body">
              I agree to the{" "}
              <a
                href="https://www.findfaye.com/client-terms"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#198282",
                  textDecoration: "none",
                }}
              >
                terms of service
              </a>
              .
            </Typography>
          </label>
          <label
            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          >
            <Checkbox
              checked={hasAcceptedBilling}
              onChange={() => {
                setHasAcceptedBilling(!hasAcceptedBilling);
              }}
            />
            <Typography variant="body">
              I agree to be auto-billed monthly and can cancel my service at any
              time.
            </Typography>
          </label>
        </Box>
        <Box sx={{ alignSelf: "flex-end", width: isMobile ? "100%" : "auto" }}>
          <Button
            sx={{ width: "100%" }}
            onClick={onContinue}
            disabled={overallLoading || !hasAcceptedBilling || !hasAcceptedToS}
          >
            Continue
          </Button>
        </Box>
      </Box>
    </NuxContentLayout>
  );
};
